import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Users, FileText, AlertTriangle, Trash2, X, Loader2, ChevronRight, Search, Download } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { useSafeMeAdminAuth } from "@/context/SafeMeAdminAuthContext";
import api, { formatApiError } from "@/lib/api";

const RISK_STYLES = {
  high: "text-red-400 border-red-400/40",
  medium: "text-yellow-400 border-yellow-400/40",
  low: "text-green-400 border-green-400/40",
  unknown: "text-fog border-white/20",
};

export default function SafeMeAdmin() {
  const auth = useSafeMeAdminAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/safeme/admin/stats"),
        api.get("/safeme/admin/users"),
      ]);
      setStats(s.data);
      setUsers(u.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const exportHighRisk = async () => {
    setExporting(true);
    try {
      const res = await api.get("/safeme/admin/export/high-risk", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "safeme-high-risk.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("High-risk report downloaded.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Delete ${user.email} and all ${user.record_count} of their records? This cannot be undone.`)) return;
    try {
      await api.delete(`/safeme/admin/users/${user.id}`);
      toast.success("User and their data deleted.");
      setActive(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <PortalShell
      authOverride={auth}
      homePath="/barecord/admin/login"
      roleLabel="BArecord Admin"
      headerTagline="BArecord · Owner Oversight"
      brand={
        <span className="flex items-center gap-2.5">
          <ShieldCheck size={18} className="text-paper" strokeWidth={1.6} />
          <span className="font-display text-lg tracking-tight text-paper">
            BArecord <span className="text-fog">Admin</span>
          </span>
        </span>
      }
      eyebrow="BArecord · Owner Oversight"
      title="All BArecord users & intelligence"
      subtitle="Oversee every BArecord account, drill into captured communications and risk scores, and remove users along with their data."
    >
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden mb-10" data-testid="safeme-admin-stats">
          <Stat label="Users" value={stats.total_users} icon={<Users size={13} />} />
          <Stat label="Records" value={stats.total_records} icon={<FileText size={13} />} />
          <Stat label="High risk" value={stats.by_risk.high} accent="text-red-400" icon={<AlertTriangle size={13} />} />
          <Stat label="Avg risk score" value={stats.avg_score} />
        </div>
      )}

      {users.length === 0 ? (
        <Empty text="No BArecord users have registered yet." />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="flex-1 flex items-center gap-2 bg-surface border border-white/10 rounded-full px-4">
              <Search size={16} className="text-fog" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search users by email, name or company…"
                data-testid="safeme-admin-search"
                className="flex-1 bg-transparent py-2.5 text-sm text-paper outline-none placeholder:text-fog/60"
              />
            </div>
            <button
              onClick={exportHighRisk}
              disabled={exporting}
              data-testid="safeme-admin-export-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-paper text-sm font-medium px-5 py-2.5 hover:bg-white/5 transition-colors disabled:opacity-60"
            >
              {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              Export high-risk CSV
            </button>
          </div>

          {(() => {
            const q = query.trim().toLowerCase();
            const filtered = q
              ? users.filter((u) =>
                  `${u.email} ${u.name} ${u.company}`.toLowerCase().includes(q)
                )
              : users;
            if (filtered.length === 0) {
              return <Empty text="No users match your search." />;
            }
            return (
              <div className="space-y-3" data-testid="safeme-admin-users">
                {filtered.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-surface p-5">
                    <button onClick={() => setActive(u)} data-testid={`safeme-admin-user-${u.id}`} className="flex-1 text-left min-w-0">
                      <p className="font-display text-lg tracking-tight text-paper truncate">{u.name || u.email}</p>
                      <p className="text-sm text-fog mt-1 truncate">{u.email}{u.company ? ` · ${u.company}` : ""}</p>
                    </button>
                    <div className="flex items-center gap-5 shrink-0">
                      <div className="text-right">
                        <p className="font-display text-xl tabular-nums text-paper">{u.record_count}</p>
                        <p className="text-[10px] uppercase tracking-widest text-fog">records</p>
                      </div>
                      {u.high_risk_count > 0 && (
                        <div className="text-right">
                          <p className="font-display text-xl tabular-nums text-red-400">{u.high_risk_count}</p>
                          <p className="text-[10px] uppercase tracking-widest text-fog">high</p>
                        </div>
                      )}
                      <button onClick={() => deleteUser(u)} data-testid={`safeme-admin-delete-user-${u.id}`} className="text-fog hover:text-red-400 transition-colors" title="Delete user">
                        <Trash2 size={18} />
                      </button>
                      <button onClick={() => setActive(u)} className="text-fog hover:text-paper transition-colors" title="View records">
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </>
      )}

      {active && <UserDrawer user={active} onClose={() => setActive(null)} onDeleteUser={() => deleteUser(active)} />}
    </PortalShell>
  );
}

function Stat({ label, value, accent = "text-paper", icon }) {
  return (
    <div className="bg-surface p-6">
      <div className={`font-display text-3xl lg:text-4xl tracking-tighter ${accent}`}>{value}</div>
      <div className="mt-2 text-xs uppercase tracking-widest text-fog flex items-center gap-1.5">{icon}{label}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center text-sm text-fog">{text}</div>;
}

function useEscape(onClose) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
}

function UserDrawer({ user, onClose, onDeleteUser }) {
  useEscape(onClose);
  const [records, setRecords] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get(`/safeme/admin/users/${user.id}/records`)
      .then(({ data }) => setRecords(data))
      .catch((err) => { toast.error(formatApiError(err.response?.data?.detail)); setRecords([]); });
  }, [user.id]);

  const openDetail = async (r) => {
    try {
      const { data } = await api.get(`/safeme/admin/records/${r.id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="safeme-admin-user-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0d0d0f] p-7 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="min-w-0">
            <h2 className="font-display text-2xl tracking-tight text-paper truncate">{user.name || user.email}</h2>
            <p className="text-sm text-fog mt-1 truncate">{user.email}{user.company ? ` · ${user.company}` : ""}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button onClick={onDeleteUser} data-testid="safeme-admin-modal-delete" className="text-fog hover:text-red-400 transition-colors" title="Delete user"><Trash2 size={18} /></button>
            <button onClick={onClose} data-testid="safeme-admin-modal-close" className="text-fog hover:text-paper transition-colors"><X size={18} /></button>
          </div>
        </div>

        {records === null ? (
          <div className="flex items-center justify-center gap-2 text-sm text-fog py-10"><Loader2 size={16} className="animate-spin" /> Loading records…</div>
        ) : records.length === 0 ? (
          <Empty text="This user has not captured any communications." />
        ) : (
          <div className="space-y-3" data-testid="safeme-admin-user-records">
            {records.map((r) => (
              <button key={r.id} onClick={() => openDetail(r)} data-testid={`safeme-admin-record-${r.id}`} className="w-full text-left rounded-xl border border-white/10 bg-surface p-4 hover:bg-[#232326] transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-widest text-fog border border-white/15 rounded-full px-2 py-0.5 capitalize">{r.type}</span>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${RISK_STYLES[r.risk?.level] || RISK_STYLES.unknown}`}>{r.risk?.level} risk</span>
                    </div>
                    <p className="mt-2 text-paper truncate">{r.title}</p>
                    <p className="text-xs text-fog mt-0.5 truncate">{r.participants || "—"} · {r.occurred_at}</p>
                  </div>
                  <div className="font-display text-xl tabular-nums text-fog shrink-0">{r.risk?.score ?? "—"}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {detail && <RecordDetail record={detail} onClose={() => setDetail(null)} />}
      </div>
    </div>
  );
}

function RecordDetail({ record, onClose }) {
  useEscape(onClose);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="safeme-admin-record-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0d0d0f] p-7 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-widest text-fog border border-white/15 rounded-full px-2 py-0.5 capitalize">{record.type}</span>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${RISK_STYLES[record.risk?.level] || RISK_STYLES.unknown}`}>{record.risk?.level} risk · {record.risk?.score}</span>
            </div>
            <h2 className="mt-3 font-display text-2xl tracking-tight text-paper">{record.title}</h2>
            <p className="text-sm text-fog mt-1">{record.participants || "—"} · {record.occurred_at}</p>
          </div>
          <button onClick={onClose} className="text-fog hover:text-paper transition-colors shrink-0"><X size={18} /></button>
        </div>
        {record.risk?.summary && (
          <div className="mt-5 rounded-xl border border-white/10 bg-surface p-4">
            <p className="text-xs uppercase tracking-widest text-fog mb-2 flex items-center gap-2"><ShieldCheck size={13} /> AI risk assessment</p>
            <p className="text-sm text-paper/90 leading-relaxed">{record.risk.summary}</p>
            {record.risk?.reasons?.length > 0 && (
              <ul className="mt-3 space-y-1 list-disc pl-5">
                {record.risk.reasons.slice(0, 6).map((r, i) => <li key={i} className="text-xs text-fog leading-relaxed">{r}</li>)}
              </ul>
            )}
          </div>
        )}
        <div className="mt-5">
          <p className="text-xs uppercase tracking-widest text-fog mb-2 flex items-center gap-2"><FileText size={13} /> Content</p>
          <p className="text-sm text-fog leading-relaxed whitespace-pre-wrap">{record.content}</p>
        </div>
      </div>
    </div>
  );
}
